import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, AlertCircle, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { consentRedirectUrl } from '../lib/oauthConsent';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { ConsentClientDetails } from '../components/oauth/ConsentClientDetails';
import { ConsentError, ConsentHandedBack } from '../components/oauth/ConsentNotices';

type AuthorizationDetails = {
  authorization_id: string;
  redirect_uri?: string;
  client: {
    id: string;
    name?: string;
    uri?: string;
    logo_uri?: string;
  };
  user: {
    id: string;
    email: string;
  };
  scope?: string;
};

const OAuthConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const authorizationId = params.get('authorization_id');

  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);
  // The client got its answer. Its callback may be an app link (claude://…): the browser opens the
  // app and stays on this page, which must then say so instead of spinning forever.
  const [handedBack, setHandedBack] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reading the request may approve it (consent already given): read it once per request and
  // never drop the answer. A second read finds it approved and fails (« authorization request
  // cannot be processed », 07/10/2026), and the client never receives its code.
  const readFor = useRef<string | null>(null);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (authLoading) return;

    if (!authorizationId) {
      setError('Paramètre authorization_id manquant.');
      setLoading(false);
      return;
    }

    if (!userId) {
      const next = `/oauth/consent?authorization_id=${encodeURIComponent(authorizationId)}`;
      navigate(`/login?redirect=${encodeURIComponent(next)}`, { replace: true });
      return;
    }

    if (readFor.current === authorizationId) return;
    readFor.current = authorizationId;
    void (async () => {
      const { data, error: err } = await supabase.auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
      if (err || !data) {
        setError(err?.message ?? "Impossible de charger la demande d'autorisation.");
        setLoading(false);
        return;
      }
      // Consent already given: approved by the server, back to the client with its code.
      const redirectUrl = consentRedirectUrl(data);
      if (redirectUrl) {
        setHandedBack('approve');
        window.location.href = redirectUrl;
        return;
      }
      // `data.redirect_uri` is the client's registered callback, without a code: never follow it.
      // Only approve / deny — or the redirect above — hand back the signed redirect_url.
      setDetails(data as AuthorizationDetails);
      setLoading(false);
    })();
  }, [authorizationId, userId, authLoading, navigate]);

  const decide = async (action: 'approve' | 'deny') => {
    if (!authorizationId || submitting !== null) return;
    setSubmitting(action);
    setError(null);

    const { data, error: err } = action === 'approve'
      ? await supabase.auth.oauth.approveAuthorization(authorizationId)
      : await supabase.auth.oauth.denyAuthorization(authorizationId);

    if (err || !data) {
      setError(err?.message ?? 'La décision n\'a pas pu être enregistrée.');
      setSubmitting(null);
      return;
    }
    setHandedBack(action);
    window.location.href = data.redirect_url;
  };

  if (handedBack !== null) return <ConsentHandedBack outcome={handedBack} />;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0066FF]" />
      </div>
    );
  }

  if (error !== null && details === null) {
    return <ConsentError message={error} onBack={() => navigate('/admin')} />;
  }

  if (details === null) return null;

  const clientName = details.client.name ?? 'Application tierce';

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6 border-b border-neutral-100">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-neutral-900 text-center">
            Autoriser l'accès à votre compte ?
          </h1>
          <p className="mt-2 text-sm text-neutral-600 text-center">
            <strong>{clientName}</strong> demande la permission d'accéder à votre compte
            AutoQuote.
          </p>
        </div>

        <div className="p-6 space-y-5">
          <ConsentClientDetails
            clientName={clientName}
            clientUri={details.client.uri ?? ''}
            clientLogo={details.client.logo_uri ?? ''}
            scope={details.scope ?? ''}
          />

          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              Connecté en tant que <strong>{details.user.email}</strong>. Autorisez uniquement les
              applications auxquelles vous faites confiance — elles pourront agir sur votre compte
              avec les permissions ci-dessus.
            </p>
          </div>

          {error !== null && (
            <div className="flex items-center p-3 border border-red-100 rounded-lg bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-neutral-100 space-y-3">
          <Button
            variant="primary"
            fullWidth
            isLoading={submitting === 'approve'}
            disabled={submitting !== null}
            leftIcon={<Check className="h-4 w-4" />}
            onClick={() => decide('approve')}
          >
            Autoriser
          </Button>
          <Button
            variant="secondary"
            fullWidth
            isLoading={submitting === 'deny'}
            disabled={submitting !== null}
            leftIcon={<X className="h-4 w-4" />}
            onClick={() => decide('deny')}
          >
            Refuser
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OAuthConsentPage;
